/**
 * `post-signup-offer` — one honest Pro offer right after a seeker signs up.
 *
 * Evidence (PostHog project 342523, humans only, 2026-10-17..11-01,
 * scripts/research/revenue-forensics-2026-11/data/posthog-funnel-30d-2026-11-01.tsv):
 * 124 human signups, 17 pricing views, 1 checkout start, 0 paid. 86% of new
 * seekers never see an offer at all; the top post-signup exits are /seeker,
 * /jobs and /jobs/:id.
 *
 * Moments (at most two per new account, never a wall):
 *  1. `landing` — the first page after sign-up. `/signup` appends
 *     `?signup_alert=` to every seeker callback (signup-auto-alert), so the
 *     param marks the post-auth landing; the server then confirms the account
 *     is under 24 h old.
 *  2. `job_detail` — the first `/jobs/:id` view after a dismiss on landing.
 *     Remembered in localStorage only, so no other page pays a server call.
 *
 * The PostHog multivariate flag is the SOLE switch: `offer` shows the card,
 * `control` (and off / failure / unknown) shows nothing. Both arms fire
 * `post_signup_offer_eligible {arm}` so the readout has a denominator.
 */

import { PRO_DAILY_AI_APPLIES } from "@/lib/apply/ai-apply-cap";
import {
	PRO_MONTHLY_COACHING_SESSIONS,
	PRO_MONTHLY_COVER_LETTERS,
	PRO_MONTHLY_RESUME_GENERATIONS,
} from "@/lib/billing/pro-limits";

export const POST_SIGNUP_OFFER_FLAG = "post-signup-offer";
export const POST_SIGNUP_OFFER_ARMS = ["control", "offer"] as const;
export type PostSignupOfferArm = (typeof POST_SIGNUP_OFFER_ARMS)[number];
export type PostSignupOfferMoment = "landing" | "job_detail";

/** `entrySurface` on the checkout intent (stamped onto checkout_completed). */
export const POST_SIGNUP_OFFER_ENTRY_SURFACE = "post_signup_offer";

/** Bounded wait on the per-user flag read (same budget as apply-cap-wall). */
export const POST_SIGNUP_OFFER_FLAG_TIMEOUT_MS = 2000;

/** localStorage key for the dismiss → job-detail reminder. */
export const POST_SIGNUP_OFFER_STORAGE_KEY = "ld_post_signup_offer_v1";
/** The job-detail reminder expires with the new-account window. */
export const POST_SIGNUP_OFFER_REMINDER_TTL_MS = 24 * 60 * 60 * 1000;

export function parsePostSignupOfferArm(v: unknown): PostSignupOfferArm | null {
	return typeof v === "string" &&
		(POST_SIGNUP_OFFER_ARMS as readonly string[]).includes(v)
		? (v as PostSignupOfferArm)
		: null;
}

/** `/id/jobs/<slug>--<publicId>` (buildJobUrl shape). Listing pages never match. */
const JOB_DETAIL_PATH = /^\/[a-z]{2}\/jobs\/[^/]+--[^/]+\/?$/;

export function isJobDetailPath(pathname: string | null | undefined): boolean {
	return typeof pathname === "string" && JOB_DETAIL_PATH.test(pathname);
}

/** Stored after a landing dismiss: the job-detail page may ask once more. */
export type ReminderState = { state: "remind"; until: number };

export function parseReminder(
	raw: string | null | undefined,
	now: number,
): ReminderState | null {
	if (!raw) return null;
	try {
		const v = JSON.parse(raw) as Partial<ReminderState>;
		if (v?.state !== "remind" || typeof v.until !== "number") return null;
		return v.until > now ? { state: "remind", until: v.until } : null;
	} catch {
		return null;
	}
}

export function buildReminder(now: number): string {
	return JSON.stringify({
		state: "remind",
		until: now + POST_SIGNUP_OFFER_REMINDER_TTL_MS,
	} satisfies ReminderState);
}

/** Product per market: Indonesia leads with the annual one-time plan (all 6
 * annual payers stayed, 0 of 8 monthly payers renewed — paywall-aha report
 * 2026-10-23, same choice as the Hunt upgrade card); the global rail is the
 * monthly membership. */
export type OfferProduct = "pro_annual" | "pro_monthly";

export function productForMarket(market: string): OfferProduct {
	return market === "global" ? "pro_monthly" : "pro_annual";
}

export type OfferCopy = {
	title: string;
	features: string[];
	priceLine: string;
	cta: string;
	dismiss: string;
	compare: string;
	busy: string;
	error: string;
};

/**
 * Card copy. Every feature line is a real Pro allowance read from the same
 * constants entitlements enforce; `price` comes from the server, resolved by
 * the same `resolvePrice()` call `createCheckout` charges from.
 */
export function buildOfferCopy(opts: {
	locale: string;
	product: OfferProduct;
	price: string;
}): OfferCopy {
	const isEn = opts.locale === "en";
	const annual = opts.product === "pro_annual";
	if (isEn) {
		return {
			title: "Your account is ready. Want Pro to apply faster?",
			features: [
				`The AI drafts up to ${PRO_DAILY_AI_APPLIES} applications a day for you`,
				`${PRO_MONTHLY_COVER_LETTERS} tailored cover letters and ${PRO_MONTHLY_RESUME_GENERATIONS} tailored CVs a month`,
				`${PRO_MONTHLY_COACHING_SESSIONS} interview practice sessions a month`,
			],
			priceLine: annual
				? `${opts.price} for a full year. Pay once, no monthly bill.`
				: `${opts.price} a month. Cancel anytime.`,
			cta: annual ? "Get Pro for a year" : "Get Pro",
			dismiss: "Not now",
			compare: "See all plans",
			busy: "Starting checkout…",
			error: "Couldn't start checkout. Please try again.",
		};
	}
	return {
		title: "Akunmu sudah siap. Mau Pro supaya melamar lebih cepat?",
		features: [
			`AI menyiapkan sampai ${PRO_DAILY_AI_APPLIES} lamaran sehari untukmu`,
			`${PRO_MONTHLY_COVER_LETTERS} cover letter dan ${PRO_MONTHLY_RESUME_GENERATIONS} CV yang disesuaikan per bulan`,
			`${PRO_MONTHLY_COACHING_SESSIONS} sesi latihan wawancara per bulan`,
		],
		priceLine: annual
			? `${opts.price} untuk setahun penuh. Bayar sekali, tanpa tagihan bulanan.`
			: `${opts.price} per bulan. Berhenti kapan saja.`,
		cta: annual ? "Ambil Pro setahun" : "Ambil Pro",
		dismiss: "Nanti saja",
		compare: "Lihat semua paket",
		busy: "Memulai checkout…",
		error: "Gagal memulai checkout. Coba lagi.",
	};
}
